import { itemFieldNames } from './events.js';
import { fieldValue, isArchived, isDeleted, orderedPhotos, resolveTagId } from './merge.js';
import { intentionHistory } from './review.js';
import { buildTree, pathTo } from './taxonomy.js';
/** Bumped only on a breaking change to the shape, so an importer can refuse early. */
export const EXPORT_FORMAT_VERSION = 1;
const cellValues = (record) => Object.fromEntries([...record.fields].map(([key, cell]) => [key, cell.v]));
const entityRows = (records) => [...records]
    .filter(([, record]) => record.lifecycle?.present !== false)
    .map(([id, record]) => {
    const values = cellValues(record);
    return {
        id,
        name: typeof values['name'] === 'string' ? values['name'] : '',
        parentId: typeof values['parentId'] === 'string' ? values['parentId'] : null,
    };
});
function exportItem(manifest, item, trees) {
    const { categories, locations } = trees;
    const categoryId = fieldValue(item, 'categoryId');
    const locationId = fieldValue(item, 'locationId');
    const fields = {};
    for (const field of itemFieldNames) {
        const value = item.fields[field]?.v;
        if (value !== undefined)
            fields[field] = value;
    }
    return {
        itemId: item.itemId,
        fields,
        // Paths rather than ids alone: an id is meaningless outside this export, and
        // a reader opening the CSV in a spreadsheet needs the words.
        categoryPath: typeof categoryId === 'string' ? pathTo(categories, categoryId) : [],
        locationPath: typeof locationId === 'string' ? pathTo(locations, locationId) : [],
        tags: [...item.tags]
            .filter(([, applied]) => applied.present)
            .map(([tagId]) => manifest.tags.get(resolveTagId(manifest, tagId))?.displayName?.v ?? '')
            .filter((name, index, all) => name !== '' && all.indexOf(name) === index)
            .sort((a, b) => a.localeCompare(b)),
        currentIntention: item.currentIntention,
        intentions: intentionHistory(item).map((record) => ({
            intention: record.intention,
            reason: record.reason ?? null,
            reviewAgainOn: record.reviewAgainOn ?? null,
            // The clock, not a wall time: it is the ordering authority, and a reader
            // reconstructing the sequence needs the thing the sequence was built from.
            at: record.hlc,
        })),
        photos: orderedPhotos(item).map((photo) => ({
            photoId: photo.photoId,
            originalKey: photo.metadata?.v.originalKey ?? null,
            derivativeKey: photo.metadata?.v.derivativeKey ?? null,
            width: photo.metadata?.v.width ?? null,
            height: photo.metadata?.v.height ?? null,
            byteSize: photo.metadata?.v.byteSize ?? null,
            uploaded: photo.uploaded?.v === true,
        })),
        release: item.release?.v ?? null,
        // Exported rather than filtered out. A deleted item is recoverable for 30
        // days (FR-3.8) and an archived one is part of the history; dropping either
        // would make the export a smaller thing than the account.
        deleted: isDeleted(item),
        archived: isArchived(item),
    };
}
/** FR-11.1: every field, every history, and references to every photo. */
export function exportDocument(manifest, options = {}) {
    const trees = {
        categories: buildTree(manifest.categories),
        locations: buildTree(manifest.locations),
    };
    const items = [...manifest.items.values()].map((item) => exportItem(manifest, item, trees));
    return {
        format: 'intentionalist-export',
        version: EXPORT_FORMAT_VERSION,
        exportedAt: options.exportedAt ?? new Date().toISOString(),
        counts: {
            items: items.length,
            events: options.events?.length ?? 0,
            photos: items.reduce((sum, item) => sum + item.photos.length, 0),
        },
        items,
        categories: entityRows(manifest.categories),
        locations: entityRows(manifest.locations),
        tags: [...manifest.tags]
            .filter(([, tag]) => tag.lifecycle?.present !== false)
            .map(([id, tag]) => ({
            id,
            displayName: tag.displayName?.v ?? '',
            mergedInto: tag.mergedInto?.v ?? null,
        })),
        customFields: [...manifest.customFieldDefs].map(([id, record]) => ({
            id,
            ...cellValues(record),
        })),
        investments: [...manifest.investments].map(([id, record]) => ({
            id,
            ...cellValues(record),
            replaces: [...record.links].filter(([, link]) => link.present).map(([itemId]) => itemId),
        })),
        ...(options.events === undefined ? {} : { events: [...options.events] }),
    };
}
// ---------------------------------------------------------------------------
// CSV (FR-11.2)
// ---------------------------------------------------------------------------
/**
 * One cell, quoted when it has to be and defanged when it looks like a formula.
 *
 * The quoting is RFC 4180. The leading apostrophe is not: a cell beginning `=`,
 * `+`, `-`, `@`, tab or carriage return is executed as a formula by Excel and
 * several others when the file is opened, which turns an exported note into a
 * command. The export's whole purpose is that the data can be opened elsewhere,
 * so it has to be safe to open elsewhere.
 */
export function csvCell(value) {
    if (value === null || value === undefined)
        return '';
    let text = typeof value === 'object' ? JSON.stringify(value) : String(value);
    if (/^[=+\-@\t\r]/.test(text))
        text = `'${text}`;
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}
export const csvRow = (cells) => cells.map(csvCell).join(',');
const moneyCell = (amount) => amount === null || amount === undefined ? '' : (amount.minor / 100).toFixed(2);
const currencyCell = (amount) => amount === null || amount === undefined ? '' : amount.currency;
/**
 * The columns, in a fixed order.
 *
 * Money is split into an amount and a currency column rather than written as
 * "300.00 USD": a spreadsheet can sum the first and cannot parse the second,
 * and NFR-FUNC-3 requires the currency to travel with the figure rather than be
 * implied.
 */
const COLUMNS = [
    { header: 'item_id', of: (item) => item.itemId },
    { header: 'name', of: (item) => item.fields['name'] },
    { header: 'brand', of: (item) => item.fields['brand'] },
    { header: 'model', of: (item) => item.fields['model'] },
    { header: 'color', of: (item) => item.fields['color'] },
    { header: 'size', of: (item) => item.fields['size'] },
    { header: 'material', of: (item) => item.fields['material'] },
    { header: 'condition', of: (item) => item.fields['condition'] },
    { header: 'quantity', of: (item) => item.fields['quantity'] ?? 1 },
    { header: 'category', of: (item) => item.categoryPath.join(' / ') },
    { header: 'location', of: (item) => item.locationPath.join(' / ') },
    { header: 'tags', of: (item) => item.tags.join('; ') },
    { header: 'acquired_on', of: (item) => item.fields['acquiredOn'] },
    { header: 'acquired_from', of: (item) => item.fields['acquiredFrom'] },
    { header: 'purchase_price', of: (item) => moneyCell(item.fields['purchasePrice']) },
    {
        header: 'purchase_currency',
        of: (item) => currencyCell(item.fields['purchasePrice']),
    },
    // Named as an estimate wherever it appears, per NFR-SAFE-1. No column totals
    // these, and nothing here is laid out as a valuation.
    {
        header: 'estimated_value_user_supplied',
        of: (item) => moneyCell(item.fields['estimatedValue']),
    },
    {
        header: 'estimated_value_currency',
        of: (item) => currencyCell(item.fields['estimatedValue']),
    },
    { header: 'intention', of: (item) => item.currentIntention ?? 'unreviewed' },
    { header: 'photo_count', of: (item) => item.photos.length },
    { header: 'notes', of: (item) => item.fields['notes'] },
    { header: 'protected', of: (item) => item.fields['protected'] ?? false },
    {
        header: 'released_outcome',
        of: (item) => item.release?.outcome,
    },
    { header: 'released_on', of: (item) => item.release?.onDate },
    { header: 'deleted', of: (item) => item.deleted },
];
export const CSV_HEADERS = COLUMNS.map((column) => column.header);
/**
 * The catalog as CSV, or any subset of it (FR-11.2).
 *
 * Takes already-exported items so that a filtered export is the same code path
 * as a full one: the filter belongs to the screen that has the filter, and
 * duplicating it here is how the two come to disagree.
 *
 * CRLF line endings, as RFC 4180 specifies -- the format is for other people's
 * tools, so it follows their rule rather than this platform's.
 */
export function itemsCsv(items) {
    const lines = [csvRow(CSV_HEADERS)];
    for (const item of items)
        lines.push(csvRow(COLUMNS.map((column) => column.of(item))));
    return `${lines.join('\r\n')}\r\n`;
}
/**
 * Every uploaded photo original, with a filename a human can read.
 *
 * Originals rather than the display derivatives, because FR-11.3 asks for the
 * captured resolution. A photo still queued on the device is omitted: there is
 * nothing in S3 to sign yet, and listing it would produce a download that 404s.
 */
export function photoExportRows(manifest) {
    const rows = [];
    for (const item of manifest.items.values()) {
        const name = String(fieldValue(item, 'name') ?? 'item');
        for (const [index, photo] of orderedPhotos(item).entries()) {
            if (photo.uploaded?.v !== true)
                continue;
            const key = photo.metadata?.v.originalKey;
            if (typeof key !== 'string' || key === '')
                continue;
            const extension = key.includes('.') ? key.slice(key.lastIndexOf('.')) : '';
            rows.push({
                itemId: item.itemId,
                itemName: name,
                photoId: photo.photoId,
                key,
                // Slugged, because these become filenames on someone else's filesystem
                // and an item called "Dad's chair / 2" would not survive as one.
                suggestedName: `${slug(name)}-${index + 1}-${photo.photoId.slice(0, 8)}${extension}`,
            });
        }
    }
    return rows;
}
const slug = (text) => text
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .toLowerCase()
    .slice(0, 60) || 'item';
/** The photo manifest as CSV, so the URLs can be fed to anything. */
export function photoManifestCsv(rows) {
    const lines = [csvRow(['item_id', 'item_name', 'photo_id', 'key', 'filename', 'url'])];
    for (const row of rows) {
        lines.push(csvRow([row.itemId, row.itemName, row.photoId, row.key, row.suggestedName, row.url]));
    }
    return `${lines.join('\r\n')}\r\n`;
}
/**
 * A downloader script, which is what makes the photo export usable.
 *
 * The amended NFR-FLEX-1 is explicit that the bytes travel at the speed of the
 * user's own connection and that the service does not assemble an archive. That
 * is only a fair trade if the user is handed something that does the fetching,
 * rather than a list of 2,000 URLs and an afternoon.
 *
 * `curl` with `--fail` and `--continue-at`, so a run that dies halfway can be
 * repeated: the URLs expire within the hour, which the script says out loud.
 */
export function downloaderScript(rows, expiresInSeconds) {
    const minutes = Math.floor(expiresInSeconds / 60);
    return [
        '#!/bin/sh',
        '# Downloads the photo originals from an Intentionalist export.',
        '#',
        `# These links expire about ${minutes} minutes after the export was made. If the`,
        '# run fails with 403, export again for fresh links and re-run -- already',
        '# downloaded files are skipped rather than fetched twice.',
        'set -eu',
        'mkdir -p photos',
        '',
        ...rows.map((row) => `curl --fail --location --continue-at - --create-dirs --output ${shellQuote(`photos/${row.suggestedName}`)} ${shellQuote(row.url)}`),
        '',
        `echo "Done: ${rows.length} photo${rows.length === 1 ? '' : 's'} in ./photos"`,
        '',
    ].join('\n');
}
/**
 * Single-quoted for POSIX sh, with embedded quotes closed and reopened.
 *
 * Presigned URLs carry `&` and `?`, and a filename comes from a name the user
 * typed. Neither may reach a shell unquoted.
 */
const shellQuote = (text) => `'${text.replace(/'/g, `'\\''`)}'`;
//# sourceMappingURL=export.js.map